import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { categories, articles, siteConfig } from '@/mocks/data';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryArticles = articles.filter((a) => a.category === slug);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - ${siteConfig.name}`;
    } else {
      document.title = `分类未找到 - ${siteConfig.name}`;
    }
  }, [category]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  if (!category) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <div className="pt-20 pb-16 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center py-20">
            <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-4">分类未找到</h1>
            <p className="text-foreground-600 mb-6">你访问的分类页面不存在</p>
            <Link
              to="/"
              className="inline-flex px-5 py-2.5 bg-primary-500 text-white rounded-md font-medium text-sm hover:bg-primary-600 transition-colors duration-200 whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <header className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={category.imageUrl}
            alt={category.name}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/20 to-black/20"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-7xl mx-auto">
          <h1 className="text-2xl md:text-4xl font-heading font-bold text-white mb-2">{category.name}</h1>
          <p className="text-white/80 text-sm md:text-base max-w-xl">{category.description}</p>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-4">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: category.name },
          ]}
        />
      </div>

      {/* Category Tabs */}
      <div className="max-w-7xl mx-auto px-4 md:px-6 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap ${
                cat.slug === slug
                  ? 'bg-primary-500 text-white'
                  : 'bg-background-100 text-foreground-700 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>
      </div>

      <main className="py-6 md:py-10 px-4 md:px-6">
        <div className="max-w-7xl mx-auto">
          {categoryArticles.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
              {categoryArticles.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="group bg-background-100 rounded-lg overflow-hidden"
                >
                  <div className="overflow-hidden aspect-[16/10]">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs text-foreground-500">{article.readTime}</span>
                      <span className="text-xs text-foreground-400">·</span>
                      <span className="text-xs text-foreground-500">{article.publishDate}</span>
                    </div>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                      {article.title}
                    </h3>
                    <p className="text-sm text-foreground-600 line-clamp-2 leading-relaxed">{article.excerpt}</p>
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {article.tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-secondary-100 text-secondary-700 rounded-full whitespace-nowrap">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <p className="text-foreground-600">该分类暂无文章</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}